import { Component, OnInit, TemplateRef } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { HttpClient } from '@angular/common/http';
import { AuthService } from 'src/app/services/auth.service';
import { StorageService } from 'src/app/services/storage-service.service';
import { GraphqlService } from 'src/app/services/graphql.service';
import { gql } from 'apollo-angular';
import { environment } from 'src/environments/environment';
import { ShoppingCartComponent } from 'src/app/modules/shopping-cart-module/shopping-cart/shopping-cart.component';
import { TranslateService } from '@ngx-translate/core';
import { ThemeService } from 'src/app/services/theme.service';
// import { log } from 'console';

interface SubscriptionPlan {
  uuid: string;
  name: string;
  description?: string;
  price: string;
  durationDays: number;
  maxUsers: number;
  maxProducts: number;
}

const GET_PUBLIC_PLANS = gql`
  query GetPublicPlans {
    getPublicPlans {
      uuid
      name
      description
      price
      durationDays
      maxUsers
      maxProducts
    }
  }
`;

// Product interface
interface Product {
  id: string;
  name: string;
  description: string;
  price: number;
  discount?: number;
  category: string;
  rating: number;
  reviews: number;
  inStock: boolean;
  isNew: boolean;
  isTopProduct: boolean;
  icon: string;
  imageUrl?: string;
  quantity?: number;
}

// User type enum - adjust path based on your project structure
enum UserTypeEnum {
  CUSTOMER = 'CUSTOMER',
  SELLER = 'SELLER',
  ADMIN = 'ADMIN'
}

@Component({
  selector: 'app-landing-page',
  templateUrl: './landing-page.component.html',
  styleUrls: ['./landing-page.component.scss'],
  standalone: false
})
export class LandingPageComponent implements OnInit {
  // Exposed for the template's "delete account" link, which points directly
  // at the backend's own public, unauthenticated deletion-request page —
  // required by Play Store's Data Safety form for users locked out of the app.
  readonly deleteAccountUrl = `${environment.SERVER_URL}/core/delete-account/`;

  readonly playStoreUrl = 'https://play.google.com/store/apps/details?id=com.akiliunde.novapoint';

  isLoading: boolean = false;
  form: UntypedFormGroup;
  mobileMenuOpen = false;
  showLoginForm = false;
  showSignupForm = false;
  showSellerLogin = false;
 // Loading states
  loading = false;

  signupForm!: UntypedFormGroup;
  sellerForm!: UntypedFormGroup;

  // Product categories
  selectedCategory: string = 'top';
  productCategories = [
    { id: 'top', nameKey: 'landing.products.category_top', icon: 'star' },
    { id: 'new', nameKey: 'landing.products.category_new', icon: 'fiber_new' },
    { id: 'offers', nameKey: 'landing.products.category_offers', icon: 'local_offer' }
  ];

  // Shopping cart
  cartItems: Product[] = [];
  cartItemsCount = 0;

  // Products data
  products: Product[] = [
    // Top Products
    {
      id: '1',
      name: 'Premium Wireless Headphones',
      description: 'High-quality wireless headphones with noise cancellation and 30-hour battery life.',
      price: 250000,
      discount: 20,
      category: 'Electronics',
      rating: 5,
      reviews: 128,
      inStock: true,
      isNew: false,
      isTopProduct: true,
      icon: 'headphones',
      imageUrl: '/assets/products/headphones.jpg'
    },
    {
      id: '2',
      name: 'Smart Fitness Watch',
      description: 'Track your fitness goals with GPS, heart rate monitor, and sleep tracking.',
      price: 180000,
      discount: 0,
      category: 'Wearables',
      rating: 4,
      reviews: 95,
      inStock: true,
      isNew: false,
      isTopProduct: true,
      icon: 'watch',
      imageUrl: '/assets/products/watch.jpg'
    },
    {
      id: '3',
      name: 'Professional Camera Kit',
      description: 'Complete camera kit with lens, tripod, and carrying case for professionals.',
      price: 850000,
      discount: 15,
      category: 'Photography',
      rating: 5,
      reviews: 67,
      inStock: true,
      isNew: false,
      isTopProduct: true,
      icon: 'camera_alt',
      imageUrl: '/assets/products/camera.jpg'
    },
    {
      id: '4',
      name: 'Gaming Laptop Pro',
      description: 'High-performance gaming laptop with RTX graphics and 32GB RAM.',
      price: 2500000,
      discount: 10,
      category: 'Computers',
      rating: 5,
      reviews: 203,
      inStock: true,
      isNew: false,
      isTopProduct: true,
      icon: 'laptop',
      imageUrl: '/assets/products/laptop.jpg'
    },

    // New Arrivals
    {
      id: '5',
      name: 'Bluetooth Speaker Pro',
      description: 'Portable bluetooth speaker with 360° sound and waterproof design.',
      price: 120000,
      discount: 0,
      category: 'Audio',
      rating: 4,
      reviews: 42,
      inStock: true,
      isNew: true,
      isTopProduct: false,
      icon: 'speaker',
      imageUrl: '/assets/products/speaker.jpg'
    },
    {
      id: '6',
      name: 'Wireless Charger Station',
      description: 'Charge multiple devices simultaneously with fast wireless charging.',
      price: 85000,
      discount: 0,
      category: 'Accessories',
      rating: 4,
      reviews: 31,
      inStock: true,
      isNew: true,
      isTopProduct: false,
      icon: 'battery_charging_full',
      imageUrl: '/assets/products/charger.jpg'
    },
    {
      id: '7',
      name: 'Smart Home Hub',
      description: 'Control all your smart home devices from one central hub.',
      price: 150000,
      discount: 0,
      category: 'Smart Home',
      rating: 5,
      reviews: 18,
      inStock: true,
      isNew: true,
      isTopProduct: false,
      icon: 'home',
      imageUrl: '/assets/products/hub.jpg'
    },
    {
      id: '8',
      name: 'USB-C Docking Station',
      description: 'Expand your laptop connectivity with multiple ports and displays.',
      price: 95000,
      discount: 0,
      category: 'Accessories',
      rating: 4,
      reviews: 54,
      inStock: true,
      isNew: true,
      isTopProduct: false,
      icon: 'router',
      imageUrl: '/assets/products/dock.jpg'
    },

    // Special Offers
    {
      id: '9',
      name: 'Mechanical Keyboard RGB',
      description: 'Gaming keyboard with customizable RGB lighting and mechanical switches.',
      price: 180000,
      discount: 30,
      category: 'Gaming',
      rating: 5,
      reviews: 156,
      inStock: true,
      isNew: false,
      isTopProduct: false,
      icon: 'keyboard',
      imageUrl: '/assets/products/keyboard.jpg'
    },
    {
      id: '10',
      name: 'Ergonomic Mouse Pro',
      description: 'Reduce strain with this ergonomic wireless mouse with precision tracking.',
      price: 65000,
      discount: 25,
      category: 'Accessories',
      rating: 4,
      reviews: 89,
      inStock: true,
      isNew: false,
      isTopProduct: false,
      icon: 'mouse',
      imageUrl: '/assets/products/mouse.jpg'
    },
    {
      id: '11',
      name: 'Webcam 4K Ultra HD',
      description: '4K webcam with auto-focus and noise-canceling microphone.',
      price: 140000,
      discount: 35,
      category: 'Video',
      rating: 5,
      reviews: 73,
      inStock: false,
      isNew: false,
      isTopProduct: false,
      icon: 'videocam',
      imageUrl: '/assets/products/webcam.jpg'
    },
    {
      id: '12',
      name: 'Portable SSD 1TB',
      description: 'Fast and reliable portable storage with USB-C connection.',
      price: 220000,
      discount: 20,
      category: 'Storage',
      rating: 5,
      reviews: 112,
      inStock: true,
      isNew: false,
      isTopProduct: false,
      icon: 'storage',
      imageUrl: '/assets/products/ssd.jpg'
    }
  ];


  plans: SubscriptionPlan[] = [];
  plansLoading = false;

  currentYear = new Date().getFullYear();

  constructor(
    private dialog: MatDialog,
    private router: Router,
    private route: ActivatedRoute,
    private http: HttpClient,
    private fb: UntypedFormBuilder,
    private auth: AuthService,
    private storageService: StorageService,
    private graphqlService: GraphqlService,
    private translate: TranslateService,
    private themeService: ThemeService
  ) {}

  openAppStoreComingSoon(template: TemplateRef<unknown>): void {
    this.dialog.open(template, { width: '380px', maxWidth: '92vw' });
  }

  get isDarkTheme(): boolean {
    return this.themeService.isDark;
  }

  get currentLang(): string {
    return this.translate.currentLang || this.translate.getDefaultLang() || 'en';
  }

  toggleTheme(): void {
    this.themeService.toggle();
  }

  toggleLanguage(): void {
    const next = this.currentLang === 'en' ? 'sw' : 'en';
    this.translate.use(next);
    localStorage.setItem('lang', next);
  }

  async ngOnInit(): Promise<void> {
    // Someone already logged in (including a returning "remember me"
    // session past the access token's 30-minute lifetime, which
    // hasValidSession() silently refreshes) has no reason to see the
    // marketing pitch again — send them straight back in.
    if (await this.auth.hasValidSession()) {
      this.router.navigate(['/dashboard']);
      return;
    }

    this.initializeForms();
    this.updateCartCount();
    this.loadPlans();

    // Deep-link support so other pages (e.g. the seller registration page's
    // "Sign in" link) can land here with the right login form already open.
    const loginType = this.route.snapshot.queryParamMap.get('login');
    if (loginType === 'seller') {
      this.toggleSellerLogin();
    } else if (loginType === 'customer') {
      this.toggleLoginForm();
    }
  }

  private async loadPlans(): Promise<void> {
    this.plansLoading = true;
    try {
      const result: any = await this.graphqlService.fetchData({ query: GET_PUBLIC_PLANS });
      this.plans = result?.data?.getPublicPlans ?? [];
    } catch (error) {
      console.error('Failed to load pricing plans:', error);
    } finally {
      this.plansLoading = false;
    }
  }

  private initializeForms(): void {
    // Customer login form
    this.form = this.fb.group({
      username: [null, Validators.required],
      password: [null, Validators.required]
    });

    // Customer signup form
    this.signupForm = this.fb.group({
      firstName: [null, Validators.required],
      lastName: [null, Validators.required],
      email: [null, [Validators.required, Validators.email]],
      phoneNumber: [null, [Validators.required, Validators.pattern(/^[0-9]{10,}$/)]],
      password: [null, [Validators.required, Validators.minLength(8)]],
      confirmPassword: [null, Validators.required]
    }, { validator: this.passwordMatchValidator });

    // Seller login form
    this.sellerForm = this.fb.group({
      email: [null, [Validators.required]],
      password: [null, Validators.required],
      rememberMe: [false]
    });
  }

  // Custom validator for password matching
  passwordMatchValidator(g: UntypedFormGroup) {
    const password = g.get('password')?.value;
    const confirmPassword = g.get('confirmPassword')?.value;
    return password === confirmPassword ? null : { mismatch: true };
  }

  // Toggle mobile menu
  toggleMobileMenu(): void {
    this.mobileMenuOpen = !this.mobileMenuOpen;
  }

  // Toggle forms
  toggleLoginForm(): void {
    this.showLoginForm = !this.showLoginForm;
    this.showSignupForm = false;
    this.showSellerLogin = false;
    if (this.showLoginForm) {
      this.form.reset();
    }
  }

  toggleSignupForm(): void {
    this.showSignupForm = !this.showSignupForm;
    this.showLoginForm = false;
    this.showSellerLogin = false;
    if (this.showSignupForm) {
      this.signupForm.reset();
    }
  }

  toggleSellerLogin(): void {
    this.showSellerLogin = !this.showSellerLogin;
    this.showLoginForm = false;
    this.showSignupForm = false;
    if (this.showSellerLogin) {
      this.sellerForm.reset();
    }
  }

  scrollToSection(sectionId: string): void {
    const el = document.getElementById(sectionId);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  // Customer login
  // async login() {
  //   if (!this.form.valid) {
  //     return;
  //   }

  //   this.loading = true;
  //   try {
  //     await this.auth.login(this.form.value);
  //     await this.auth.authRole();

  //     const userType = this.storageService.get('userType');
  //     console.log('User type:', userType);

  //     if (userType) {
  //       if (userType === UserTypeEnum.CUSTOMER) {
  //         await this.router.navigate(['', 'customer', 'dashboard']);
  //       } else if (userType === UserTypeEnum.SELLER) {
  //         await this.router.navigate(['', 'seller', 'dashboard']);
  //       } else if (userType === UserTypeEnum.ADMIN) {
  //         await this.router.navigate(['', 'admin', 'dashboard']);
  //       } else {
  //         // Default route for other user types
  //         await this.router.navigate(['', 'dashboard']);
  //       }
  //     } else {
  //       // If no user type found, redirect to login
  //       await this.router.navigate(['', 'login']);
  //     }
  //   } catch (error) {
  //     console.error('Login error:', error);
  //     // Handle error - show notification or error message
  //     this.loading = false;
  //   }
  //   this.loading = false;
  // }

  // Seller login
  async sellerLogin() {
    if (!this.sellerForm.valid) {
      return;
    }

    this.loading = true;
    try {
      // Map seller form to auth format
      const credentials = {
        username: this.sellerForm.value.email,
        password: this.sellerForm.value.password,
        rememberMe: !!this.sellerForm.value.rememberMe
      };

      await this.auth.login(credentials);
      await this.auth.authRole();

      await this.router.navigate(['/dashboard']);
    } catch (error: any) {
      console.error('Seller login error:', error);
      if (error?.code === 'email_not_verified') {
        // The backend returns the account's real email alongside this
        // code (safe to trust — the password just verified correctly),
        // so use that instead of the login field's typed value, which
        // may have been a username rather than an email.
        await this.router.navigate(['/verify-email-pending'], {
          queryParams: error?.email ? { email: error.email } : {}
        });
      }
      this.loading = false;
    }
    this.loading = false;
  }

//   async sellerLogin() {
//   // 🔴 TEMPORARY BYPASS

//           if (!this.sellerForm.valid) {
//       return;
//     }

//     this.loading = true;
//     // try {
//       // Map seller form to auth format
//       const credentials = {
//         username: this.sellerForm.value.email,
//         password: this.sellerForm.value.password
//       };

//       console.log(credentials)

//           await this.router.navigate(['', 'dashboard']);
//           console.log("login")
// }

// Customer login — there's no customer-account system on the backend yet
// (only Owner/Staff/Cashier auth). Say so honestly instead of silently
// navigating to /dashboard with no real session, which just bounces the
// user right back out once AuthGuard notices there's no token.
login(): void {
  if (!this.form.valid) {
    return;
  }
  alert('Customer accounts are coming soon! Please check back later.');
}


  // Customer signup
  async submitSignup(formValue: any) {
    if (!this.signupForm.valid) {
      console.log('Signup form is invalid');
      return;
    }

    this.loading = true;
    try {
      console.log('Customer signup:', formValue);

      // TODO: Implement actual registration API call
      // Example:
      // const response = await this.http.post(`${environment.apiUrl}/auth/signup`, formValue).toPromise();

      // For now, show success message and redirect to login
      alert('Account created successfully! Please login.');
      this.toggleLoginForm();

    } catch (error) {
      console.error('Signup error:', error);
      // Handle error - show notification or error message
    }
    this.loading = false;
  }

  // Product Category Methods
  selectCategory(categoryId: string): void {
    this.selectedCategory = categoryId;
  }

  getFilteredProducts(): Product[] {
    switch (this.selectedCategory) {
      case 'top':
        return this.products.filter(p => p.isTopProduct);
      case 'new':
        return this.products.filter(p => p.isNew);
      case 'offers':
        return this.products.filter(p => p.discount && p.discount > 0);
      default:
        return this.products;
    }
  }

  // Shopping Cart Methods
  // Storefront checkout isn't built yet (no real customer accounts, no
  // order flow) — say so honestly instead of adding to a cart that goes
  // nowhere.
  addToCart(product: Product): void {
    alert('Customer portal coming soon! Ordering will be available once it launches.');
  }

  removeFromCart(productId: string): void {
    this.cartItems = this.cartItems.filter(item => item.id !== productId);
    this.updateCartCount();
  }

  updateCartCount(): void {
    this.cartItemsCount = this.cartItems.reduce((total, item) => total + (item.quantity || 1), 0);
  }

  getCartTotal(): number {
    return this.cartItems.reduce((total, item) => {
      const price = this.getDiscountedPrice(item);
      return total + (price * (item.quantity || 1));
    }, 0);
  }

  openCart(): void {
    // Open cart dialog
    const dialogRef = this.dialog.open(ShoppingCartComponent, {
      width: '700px',
      maxWidth: '95vw',
      data: { items: this.cartItems },
      panelClass: 'shopping-cart-dialog'
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result && result.action === 'checkout') {
        console.log('Proceeding to checkout with items:', result.items);
        // Navigate to checkout page
        // this.router.navigate(['', 'checkout']);
      }
      // Update cart count in case items were removed
      this.updateCartCount();
    });
  }

  // Product Detail Methods
  viewProductDetails(product: Product): void {
    console.log('Viewing product details:', product);

    // TODO: Open product details dialog or navigate to product page
    // Example options:

    // Option 1: Navigate to product detail page
    // this.router.navigate(['', 'products', product.id]);

    // Option 2: Open dialog with product details
    // const dialogRef = this.dialog.open(ProductDetailsComponent, {
    //   width: '800px',
    //   data: { product }
    // });

    // For now, show alert
    alert(`Product: ${product.name}\nPrice: TZS ${product.price.toLocaleString()}\nCategory: ${product.category}\nRating: ${product.rating}/5 stars\n\n${product.description}`);
  }

  getDiscountedPrice(product: Product): number {
    if (product.discount) {
      return product.price * (1 - product.discount / 100);
    }
    return product.price;
  }
}
